import React, { useState, useMemo } from 'react'
import { ChevronDown, ChevronUp, Paperclip } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Textarea } from '@/components/ui/Textarea'
import { CompactScoreInput } from '@/components/performance/CompactScoreInput'
import { getIconComponent } from '@/components/performance/icon-utils'
import type { EvaluationCategory, EvaluationItem, EvaluationScore } from '@/types/performance'

export interface ThemeRowAccordionProps {
  theme: EvaluationCategory
  scores: EvaluationScore[]
  items: EvaluationItem[]
  readOnly: boolean
  id?: string
  onScoreChange?: (itemId: string, score: number) => void
  onSkip?: (itemId: string) => void
  onUnskip?: (itemId: string) => void
  onObservationChange?: (itemId: string, observation: string) => void
}

export interface ThemeKPIs {
  mediaGeral: number
  totalItens: number
  validados: number
  requerAcao: number
}

export function computeThemeKPIs(
  themes: EvaluationCategory[],
  allScores: EvaluationScore[],
  allItems: EvaluationItem[]
): ThemeKPIs {
  let totalItens = 0
  let validados = 0
  let requerAcao = 0
  let totalWeightedScore = 0
  let totalWeight = 0

  for (const theme of themes) {
    const themeItems = allItems.filter((i) => i.categoryId === theme.categoryId)
    for (const item of themeItems) {
      totalItens++
      const score = allScores.find((s) => s.itemId === item.itemId)
      if (score && score.score !== null && score.score >= 3) {
        validados++
      }
      if (!score || score.score === null || score.score < 3) {
        if (!score?.skipped) {
          requerAcao++
        }
      }
      if (score && score.score !== null && !score.skipped) {
        totalWeightedScore += score.score
        totalWeight++
      }
    }
  }

  const mediaGeral = totalWeight > 0 ? totalWeightedScore / totalWeight : 0

  return { mediaGeral, totalItens, validados, requerAcao }
}

function computeThemeAverage(scores: EvaluationScore[], items: EvaluationItem[]): number {
  let total = 0
  let count = 0
  for (const item of items) {
    const score = scores.find((s) => s.itemId === item.itemId)
    if (score && score.score !== null && !score.skipped) {
      total += score.score
      count++
    }
  }
  return count > 0 ? total / count : 0
}

function countRequerAcao(scores: EvaluationScore[], items: EvaluationItem[]): number {
  let count = 0
  for (const item of items) {
    const score = scores.find((s) => s.itemId === item.itemId)
    if (!score || (score.score === null && !score.skipped) || (score.score !== null && score.score < 3 && !score.skipped)) {
      count++
    }
  }
  return count
}

export const ThemeRowAccordion: React.FC<ThemeRowAccordionProps> = ({
  theme,
  scores,
  items,
  readOnly,
  id,
  onScoreChange,
  onSkip,
  onUnskip,
  onObservationChange,
}) => {
  const [expanded, setExpanded] = useState(false)

  const IconComponent = useMemo(() => getIconComponent(theme.icon), [theme.icon])
  const average = useMemo(() => computeThemeAverage(scores, items), [scores, items])
  const requerAcaoCount = useMemo(() => countRequerAcao(scores, items), [scores, items])
  const hasItems = items.length > 0

  const getScoreForItem = (itemId: string): EvaluationScore | undefined => {
    return scores.find((s) => s.itemId === itemId)
  }

  return (
    <div
      id={id}
      data-testid={`theme-accordion-${theme.categoryId}`}
      className="card border border-brand-gray/10 rounded-lg overflow-hidden"
    >
      {/* Header */}
      <button
        type="button"
        data-testid={`theme-header-${theme.categoryId}`}
        onClick={() => hasItems && setExpanded(!expanded)}
        disabled={!hasItems}
        className={`w-full flex items-center justify-between px-4 py-3 ${
          hasItems ? 'cursor-pointer hover:bg-brand-dark-lighter/50' : 'cursor-default'
        } transition-colors`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${theme.colorScheme?.bgClass ?? 'bg-brand-dark-lighter'}`}>
            <IconComponent className={`w-4 h-4 ${theme.colorScheme?.textClass ?? 'text-brand-gray'}`} />
          </div>
          <div className="text-left">
            <span className="text-sm font-medium text-white">{theme.name}</span>
            <span className="text-xs text-brand-gray ml-2">
              {hasItems ? `${items.length} ${items.length === 1 ? 'item' : 'itens'}` : 'Sem itens'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {requerAcaoCount > 0 && (
            <Badge
              variant="error"
              data-testid={`requer-acao-badge-${theme.categoryId}`}
            >
              ⚠ {requerAcaoCount} Requer ação
            </Badge>
          )}
          {hasItems && (
            <span className={`text-sm font-semibold ${
              average >= 3 ? 'text-brand-primary' : average > 0 ? 'text-warning' : 'text-brand-gray'
            }`}>
              {average > 0 ? average.toFixed(1) : '—'}
            </span>
          )}
          {hasItems && (
            <span data-testid={`theme-toggle-${theme.categoryId}`}>
              {expanded ? (
                <ChevronUp className="w-3 h-3 text-brand-gray" />
              ) : (
                <ChevronDown className="w-3 h-3 text-brand-gray" />
              )}
            </span>
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && hasItems && (
        <div className="border-t border-brand-gray/10 px-4 py-3 space-y-4">
          {/* Theme description */}
          {theme.description && (
            <p className="text-sm text-brand-gray italic">{theme.description}</p>
          )}

          {/* Per-item blocks */}
          {items.map((item) => {
            const score = getScoreForItem(item.itemId)
            return (
              <div
                key={item.itemId}
                className="bg-brand-dark-lighter/30 rounded-lg p-3 space-y-2"
              >
                <p className="text-sm text-white">{item.description}</p>

                <div className="flex items-center gap-4">
                  <div data-testid={`score-input-${item.itemId}`}>
                    <CompactScoreInput
                      value={score?.score ?? 0}
                      skipped={score?.skipped ?? false}
                      onChange={(newScore) => onScoreChange?.(item.itemId, newScore)}
                      onSkip={() => onSkip?.(item.itemId)}
                      onUnskip={() => onUnskip?.(item.itemId)}
                      disabled={readOnly}
                    />
                  </div>
                </div>

                <div data-testid={`observation-${item.itemId}`}>
                  <Textarea
                    placeholder="Observações..."
                    value={score?.comments ?? ''}
                    onChange={(e) => onObservationChange?.(item.itemId, e.target.value)}
                    disabled={readOnly}
                    className="form-input bg-brand-dark border-brand-gray/10 text-white min-h-[60px]"
                  />
                </div>

                {score?.evidence && (
                  <div className="flex items-center gap-1 text-xs text-brand-gray">
                    <Paperclip className="w-3 h-3" />
                    <span>Evidência anexada</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
